import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Flex } from "theme-ui";
import moment from "moment-timezone";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import { PlusIcon } from "components/svg";

import {
  getLocationById,
  getMapsAtLocation,
  getRecordsAtLocation,
  MapAtLocationSummary,
  RecordsAtLocation,
  archiveLocation,
  restoreLocation,
  deleteLocation,
  getLocationReferenceCounts,
} from "api/Locations";
import {
  archiveMapGroup,
  exportLocationMulchMaps,
  exportMulchMap,
  getMulchMap,
  importMulchMap,
  previewImportMulchMap,
  restoreMapGroup,
  saveMulchMap,
  ImportPreviewEntry,
} from "api/MulchMap";
import {
  getProfilesAtLocation,
  LocationClientProfileSummary,
} from "api/LocationClientProfiles";
import { getWorkOrders } from "api/ServicePlans";
import { downloadJson, pickJsonFile, safeFilename } from "../MulchCalculator/utils/importExport";
import AttachMapModal from "components/AttachMapModal";
import ProfileEditModal from "components/ProfileEditModal";
import ImportMapDedupModal, {
  EntryChoice as ImportEntryChoice,
} from "components/ImportMapDedupModal";
import SitePlanActivityPanel from "./components/SitePlanActivityPanel";
import LocationNotes from "./components/LocationNotes";
import ClientLocationDetail from "./ClientLocationDetail";
import UserContext from "context/User";
import { getInvoiceStatusDisplay } from "components/ui/InvoiceStatusBadge";

import styles from "./detail.module.scss";

const formatAddress = (address: any): string => {
  if (!address) return "";
  const parts = [
    address.line1,
    address.city,
    address.state,
    address.postalCode,
  ].filter(Boolean);
  return parts.join(", ");
};

/**
 * Session 20 Commit 3 \u2014 Location detail page.
 *
 * Sits between the Locations directory and the Mulch Calculator editor.
 * Shows all Maps at this Location so users pick which one to open, or
 * start a fresh Map at the same site.
 *
 * Row actions:
 *   - Open: navigate to canonical editor URL
 *   - Duplicate: fork the Map's current version into a new mapGroup
 *   - Archive: soft-delete the whole Map (all versions)
 *
 * The list intentionally omits deleted/archived Maps. To un-archive, a
 * follow-up commit will add an "Archived" toggle + restoreMapGroup call.
 *
 * Records that reference these Maps (RFQ / Quote / Project / Invoice)
 * will appear in a section below the Maps list once Commit 4 lands the
 * `mulchMapId` FK on those tables.
 */
const OperatorLocationDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [location, setLocation] = useState<any | null>(null);
  const [maps, setMaps] = useState<MapAtLocationSummary[]>([]);
  const [records, setRecords] = useState<RecordsAtLocation>({
    quoteRequests: [],
    quotes: [],
    projects: [],
    invoices: [],
    purchaseOrders: [],
  });
  const [loadingLocation, setLoadingLocation] = useState(true);
  const [loadingMaps, setLoadingMaps] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(true);
  // Work orders at this site (via the LCP's location). Maintenance
  // records live on the WO surface, not the RFQ/Quote/etc chain, so they
  // get their own related list rather than folding into RecordGroup.
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  // Phase B — profile picker state. Chip row is hidden entirely when the
  // Location has 0 or 1 profile (the ~90% common case). When multi-profile,
  // this drives the Maps + Records filters. Null = "All clients" (aggregate
  // view, useful for company-side reps who want to see everything at a site).
  const [profiles, setProfiles] = useState<LocationClientProfileSummary[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(
    null,
  );
  // Which profile is open in the edit modal. Null = closed.
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  // Which record is currently being attached to. Null = modal closed.
  const [attachTarget, setAttachTarget] = useState<{
    recordType: "quote_request" | "quote" | "project" | "invoice";
    recordId: string;
    currentMulchMapId: string | null;
  } | null>(null);

  // Session 24: pending import waiting on the dedup-choice modal. When
  // previewImportMulchMap comes back with at least one duplicate, we hold
  // the parsed payload + preview entries here and open the modal. Confirm
  // fires the real import; cancel just clears this.
  const [pendingImport, setPendingImport] = useState<{
    payload: any;
    entries: ImportPreviewEntry[];
  } | null>(null);

  // Session 24: location-level archive/delete busy flag so the header
  // buttons can show a spinner without competing with busyId (which is
  // scoped to per-Map actions).
  const [locationBusy, setLocationBusy] = useState(false);

  const refetchMaps = useCallback(async () => {
    if (!id) return;
    setLoadingMaps(true);
    const res = await getMapsAtLocation(id, showArchived);
    setMaps(Array.isArray(res) ? res : []);
    setLoadingMaps(false);
  }, [id, showArchived]);

  const refetchRecords = useCallback(async () => {
    if (!id) return;
    setLoadingRecords(true);
    const res = await getRecordsAtLocation(id);
    setRecords(res);
    setLoadingRecords(false);
  }, [id]);

  const refetchWorkOrders = useCallback(async () => {
    if (!id) return;
    setLoadingWorkOrders(true);
    // excludeInterceptor: the locationId filter is newer than the
    // deployed API on some environments — degrade this one list rather
    // than taking the whole page to the 404 screen.
    const res = await getWorkOrders({
      query: { locationId: id },
      excludeInterceptor: true,
    });
    setWorkOrders(Array.isArray(res) ? res : []);
    setLoadingWorkOrders(false);
  }, [id]);

  /**
   * Refetch profiles at this Location. Called once on mount and after
   * a save/create flow so a newly-materialized profile shows up in the
   * picker without a page reload.
   */
  const refetchProfiles = useCallback(async () => {
    if (!id) return;
    const res = await getProfilesAtLocation(id);
    setProfiles(Array.isArray(res) ? res : []);
  }, [id]);

  useEffect(() => {
    document.title = `${process.env.REACT_APP_NAME} | Location`;
    if (!id) return;
    let cancelled = false;

    (async () => {
      setLoadingLocation(true);
      // Session 24: fetch with includeDeleted=true so archived Locations
      // are reachable and the Restore workflow is usable. Live Locations
      // are unaffected.
      const loc = await getLocationById(id, { includeDeleted: true });
      if (cancelled) return;
      if (!loc) {
        setError("Location not found.");
      } else {
        setLocation(loc);
      }
      setLoadingLocation(false);
    })();

    refetchMaps();
    refetchRecords();
    refetchWorkOrders();
    refetchProfiles();
    return () => {
      cancelled = true;
    };
  }, [id, refetchMaps, refetchRecords, refetchWorkOrders, refetchProfiles]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const handleOpen = (m: MapAtLocationSummary) => {
    navigate(`/mulch-calculator/${id}/maps/${m.mapGroupId}/edit`);
  };

  const handleNewMap = () => {
    if (!id) return;
    // We're already on this Location's record — route straight to the
    // editor's alias route for this locationId. The editor's Case-B
    // bootstrap starts a fresh Map at this Location (or redirects to the
    // most recent existing Map). No coord round-trip, no landing-page
    // detour — and it works for imported sites whose coords are still
    // null, since Case B geocodes + backfills from the address on open.
    navigate(`/mulch-calculator/${id}/edit`);
  };

  /**
   * Duplicate a Map's current version into a fresh mapGroup at the same
   * Location. Frontend-driven: fetch the current version (zones + camera),
   * then save under a new mapGroupId with a prompted-for name. Backend
   * treats it as a normal save so priorCurrent logic doesn't kick in
   * (fresh mapGroupId means an empty group, so this row is v1).
   */
  const handleDuplicate = async (m: MapAtLocationSummary) => {
    if (!id) return;
    const requested = window.prompt(
      `Name for the duplicate of "${m.name || "Untitled map"}":`,
      `${m.name || "Untitled map"} (copy)`,
    );
    if (requested === null) return;
    const newName = requested.trim() || "Untitled map";

    setBusyId(m.mapGroupId);
    try {
      const source = await getMulchMap(m.currentMapId);
      if (!source) {
        setError("Failed to load source map for duplication.");
        return;
      }
      const res: any = await saveMulchMap({
        locationId: id,
        // Omit mapGroupId \u2014 backend generates a fresh one so this row is
        // v1 of a brand-new Map (not another version of the source).
        name: newName,
        clientId: source.client?.id ?? undefined,
        mapCenterLat: source.mapCenterLat ?? undefined,
        mapCenterLng: source.mapCenterLng ?? undefined,
        mapZoom: source.mapZoom ?? undefined,
        camera3D: source.camera3D ?? undefined,
        basemap: source.basemap ?? undefined,
        notes: source.notes ?? undefined,
        zones: (source.zones ?? []).map((z: any, i: number) => ({
          name: z.name ?? undefined,
          color: z.color ?? undefined,
          depthInches: z.depthInches,
          areaSqMeters: z.areaSqMeters,
          path: z.path,
          displayOffsets: z.displayOffsets ?? undefined,
          sortOrder: z.sortOrder ?? i,
        })),
      });
      if (res?.id) {
        // Post-duplicate, drop the user straight into the new Map's editor.
        // The Location detail page they came from is one back-click away.
        navigate(
          `/mulch-calculator/${id}/maps/${res.mapGroupId ?? res.id}/edit`,
        );
      } else {
        setError((res as any)?.error || "Duplicate failed.");
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleRestore = async (m: MapAtLocationSummary) => {
    setBusyId(m.mapGroupId);
    try {
      const res: any = await restoreMapGroup(m.mapGroupId);
      if (res?.success) {
        await refetchMaps();
      } else {
        setError(res?.error || "Restore failed.");
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleArchive = async (m: MapAtLocationSummary) => {
    const ok = window.confirm(
      `Archive "${m.name || "Untitled map"}"? All versions will be hidden. Records already priced against this map still render the archived version, and archive can be undone from the same page later.`,
    );
    if (!ok) return;

    setBusyId(m.mapGroupId);
    try {
      const res: any = await archiveMapGroup(m.mapGroupId);
      if (res?.success) {
        await refetchMaps();
      } else {
        setError(res?.error || "Archive failed.");
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleExportOne = async (m: MapAtLocationSummary) => {
    setBusyId(m.mapGroupId);
    try {
      const payload = await exportMulchMap(m.currentMapId);
      if (!payload) {
        setError("Export failed.");
        return;
      }
      const site = safeFilename(
        location?.name || location?.address?.line1 || "site",
      );
      const mapNameSafe = safeFilename(m.name || "map");
      downloadJson(`${site}__${mapNameSafe}__v${m.version}.json`, payload);
    } finally {
      setBusyId(null);
    }
  };

  const handleExportAll = async () => {
    if (!id) return;
    setBusyId("__export_all__");
    try {
      const payload = await exportLocationMulchMaps(id);
      if (!payload) {
        setError("Export failed.");
        return;
      }
      const site = safeFilename(
        location?.name || location?.address?.line1 || "site",
      );
      downloadJson(`${site}__all_maps.json`, payload);
    } finally {
      setBusyId(null);
    }
  };

  const handleImportHere = async () => {
    const payload = await pickJsonFile();
    if (!payload) return;
    setBusyId("__import__");
    setError(null);
    try {
      // Session 24: preview first so we can prompt about duplicates
      // before committing. Preview is side-effect-free.
      const preview: any = await previewImportMulchMap(payload);
      if (preview?.error) {
        setError(preview.error);
        return;
      }
      const hasDupes =
        Array.isArray(preview?.entries) &&
        preview.entries.some((e: ImportPreviewEntry) => e.duplicateOf !== null);
      if (hasDupes) {
        // Hand control to the modal. It will call back into
        // finalizeImport() with the rep's per-entry choices.
        setPendingImport({ payload, entries: preview.entries });
        return;
      }
      // No duplicates — straight commit, no user interruption.
      await finalizeImport(payload, undefined);
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Second half of the import flow. Fires the real import with the
   * user's per-entry choices (empty object = 'new' for every entry, same
   * as pre-Session-24 behavior). Handles the same post-import navigation
   * + unmatched-clients banner as the pre-preview version.
   */
  const finalizeImport = async (
    payload: any,
    entryChoices: Record<
      number,
      { choice: ImportEntryChoice; targetMapGroupId?: string }
    > | undefined,
  ) => {
    setBusyId("__import__");
    setError(null);
    try {
      const res: any = await importMulchMap(payload, entryChoices);
      if (res?.error) {
        setError(res.error);
        return;
      }
      if (
        Array.isArray(res?.unmatchedClientNames) &&
        res.unmatchedClientNames.length > 0
      ) {
        setError(
          `Imported successfully, but these clients from the file weren't matched in your account: ${res.unmatchedClientNames.join(", ")}. Open the affected map(s) and assign a client from the sidebar.`,
        );
      }
      if (res?.locationId && res.locationId !== id) {
        navigate(`/locations/${res.locationId}`);
        return;
      }
      await refetchMaps();
      await refetchProfiles();
    } finally {
      setBusyId(null);
    }
  };

  // ------------------------------------------------------------------------
  // Session 24: Location-level archive / restore / delete
  // ------------------------------------------------------------------------

  const handleArchiveLocation = async () => {
    if (!id) return;
    const ok = window.confirm(
      `Archive this Location? It will be hidden from the directory. Existing RFQs, Quotes, Projects, Invoices, POs, and Maps at this site keep working — archive is reversible from this page.`,
    );
    if (!ok) return;
    setLocationBusy(true);
    try {
      const res: any = await archiveLocation(id);
      if ((res as any)?.error) {
        setError((res as any).error);
        return;
      }
      // Refetch to pick up the archived state. Backend still returns the
      // Location on get; UI just flags it and offers restore.
      const loc = await getLocationById(id, { includeDeleted: true });
      if (loc) setLocation(loc);
    } finally {
      setLocationBusy(false);
    }
  };

  const handleRestoreLocation = async () => {
    if (!id) return;
    setLocationBusy(true);
    try {
      const res: any = await restoreLocation(id);
      if ((res as any)?.error) {
        setError((res as any).error);
        return;
      }
      const loc = await getLocationById(id, { includeDeleted: true });
      if (loc) setLocation(loc);
    } finally {
      setLocationBusy(false);
    }
  };

  const handleDeleteLocation = async () => {
    if (!id) return;
    // Backend enforces refs=0 before allowing hard-delete, but the UX
    // benefits from a client-side pre-check so the confirm dialog can
    // list what's blocking (and offer archive as an alternative) without
    // roundtripping a failing DELETE.
    setLocationBusy(true);
    try {
      const refs = await getLocationReferenceCounts(id);
      if (refs && refs.total > 0) {
        const parts = [
          refs.quoteRequests && `${refs.quoteRequests} RFQ(s)`,
          refs.quotes && `${refs.quotes} quote(s)`,
          refs.projects && `${refs.projects} project(s)`,
          refs.invoices && `${refs.invoices} invoice(s)`,
          refs.purchaseOrders && `${refs.purchaseOrders} PO(s)`,
          refs.mulchMaps && `${refs.mulchMaps} map(s)`,
          refs.profiles && `${refs.profiles} client profile(s)`,
        ].filter(Boolean);
        const goArchive = window.confirm(
          `This Location has downstream references and can't be deleted: ${parts.join(", ")}.\n\nArchive instead? Archive is reversible and preserves all history.`,
        );
        if (goArchive) {
          await handleArchiveLocation();
        }
        return;
      }
      const ok = window.confirm(
        `Delete this Location permanently? This can't be undone. No RFQs, Quotes, Projects, Invoices, POs, Maps, or Profiles reference this Location.`,
      );
      if (!ok) return;
      const res: any = await deleteLocation(id);
      if ((res as any)?.error) {
        setError((res as any).error);
        return;
      }
      // No refs, so nothing to navigate back to except the directory.
      navigate("/locations");
    } finally {
      setLocationBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Filters + derived state (Phase B)
  // -------------------------------------------------------------------------

  /**
   * Resolve the selected profile row from the profiles list. Null when
   * either no profile is selected ("All clients") or the selected id no
   * longer exists in the list (rare, e.g. after a client delete).
   */
  const selectedProfile = useMemo(() => {
    if (!selectedProfileId) return null;
    return profiles.find((p) => p.id === selectedProfileId) ?? null;
  }, [profiles, selectedProfileId]);

  /**
   * Maps filtered to the selected profile's client. Since profiles are
   * unique per (Location, Client) and both Maps and profiles denorm the
   * clientId, filtering by clientId is equivalent to filtering by
   * profileId — and avoids needing the profile FK on every Map row.
   * Internal-draft Maps (clientId=null) only surface in the All view.
   */
  const filteredMaps = useMemo(() => {
    if (!selectedProfile?.clientId) return maps;
    return maps.filter((m) => m.clientId === selectedProfile.clientId);
  }, [maps, selectedProfile]);

  /**
   * Records filtered by the selected profile's client. Same clientId
   * proxy as filteredMaps. Backend was extended in Phase B to include
   * `clientId` in the listRecordsAtLocation SELECTs specifically to make
   * this cheap and consistent.
   */
  const filteredRecords = useMemo(() => {
    if (!selectedProfile?.clientId) return records;
    const match = (r: any) => r.clientId === selectedProfile.clientId;
    return {
      quoteRequests: records.quoteRequests.filter(match),
      quotes: records.quotes.filter(match),
      projects: records.projects.filter(match),
      invoices: records.invoices.filter(match),
      purchaseOrders: records.purchaseOrders.filter(match),
    };
  }, [records, selectedProfile]);

  const filteredRecordsTotal =
    filteredRecords.quoteRequests.length +
    filteredRecords.quotes.length +
    filteredRecords.projects.length +
    filteredRecords.invoices.length +
    filteredRecords.purchaseOrders.length;

  const filteredWorkOrders = useMemo(() => {
    if (!selectedProfile?.clientId) return workOrders;
    return workOrders.filter(
      (w) => w?.lcp?.client?.id === selectedProfile.clientId,
    );
  }, [workOrders, selectedProfile]);

  /**
   * Session 26 Batch #9: derive client aggregation from map versions
   * (and records as a fallback) instead of from LocationClientProfile
   * rows. Since Batch #6 moved client + contacts down onto each map
   * version, the map table is the authoritative source of "which
   * clients have activity at this site." LCP rows still exist and
   * still carry per-client notes, but they no longer drive what the
   * chip row shows — they're just looked up per clientId for the Edit
   * affordance that opens the notes-only ProfileEditModal.
   *
   * Clients merge from two sources:
   *   1. mulch_map.clientId across current versions at this location
   *   2. record.clientId across RFQ / Quote / Project / Invoice / PO
   * Union of both sets. Records-with-no-map-attached still surface
   * their client on the chip row — same behavior as pre-Batch #9's
   * "unprofiled" chip, minus the amber styling (unified rendering).
   *
   * canDemote fires whenever the union is size 1 — the single-line
   * summary replaces the chip row (Batch #5 .profileSummaryLine
   * behavior preserved).
   */
  const chipRowInfo = useMemo(() => {
    const clientDisplayMap = new Map<string, string>();
    // Maps first — they're the authoritative client-at-site anchor
    // post-Batch-#6. Client name comes off the MapAtLocationSummary
    // shape (backend leftJoins client on listMapsAtLocation).
    for (const m of maps) {
      if (m.clientId && !clientDisplayMap.has(m.clientId)) {
        clientDisplayMap.set(m.clientId, m.clientName || "Unknown client");
      }
    }
    // Records fill in any client that doesn't have a current-version
    // map at this site (e.g. legacy RFQ from before mulch calculator
    // adoption, or a record whose attached map got archived).
    const collect = (rows: any[]) => {
      for (const r of rows) {
        if (r.clientId && !clientDisplayMap.has(r.clientId)) {
          clientDisplayMap.set(r.clientId, r.clientName || "Unknown client");
        }
      }
    };
    collect(records.quoteRequests);
    collect(records.quotes);
    collect(records.projects);
    collect(records.invoices);
    collect(records.purchaseOrders);

    // Lookup: clientId -> LocationClientProfileSummary. Used by chip
    // Edit affordance to open ProfileEditModal for notes. Clients with
    // activity but no profile row (unusual — saveVersion normally
    // findOrCreates one) get profile: null and no edit button.
    const profileByClientId = new Map<string, LocationClientProfileSummary>();
    for (const p of profiles) {
      if (p.clientId) profileByClientId.set(p.clientId, p);
    }

    const clients = Array.from(clientDisplayMap.entries()).map(
      ([clientId, clientName]) => ({
        clientId,
        clientName,
        profile: profileByClientId.get(clientId) ?? null,
      }),
    );

    const canDemote = clients.length === 1;
    const soleClient = canDemote ? clients[0] : null;

    return { clients, canDemote, soleClient };
  }, [maps, records, profiles]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const subtitle = useMemo(() => {
    if (loadingLocation) return "Loading\u2026";
    if (!location) return "";
    const addr = formatAddress(location.address);
    const coords =
      location.latitude != null && location.longitude != null
        ? `${Number(location.latitude).toFixed(5)}, ${Number(location.longitude).toFixed(5)}`
        : "";
    return [addr, coords].filter(Boolean).join(" \u00b7 ");
  }, [location, loadingLocation]);

  if (error && !location) {
    return (
      <>
        <Header />
        <Page title="Location">
          <Box className={styles.errorBox}>{error}</Box>
        </Page>
      </>
    );
  }

  return (
    <>
      <Header />
      <Page
        title={location?.name || (loadingLocation ? "Location" : "Location")}
        subtitle={subtitle}
      >
        {location?.deletedAt && (
          <Box
            sx={{
              mb: 16,
              p: "10px 14px",
              bg: "#FFF4E5",
              border: "1px solid #F5C782",
              borderRadius: "6px",
              fontSize: 13,
              color: "#8A5300",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <span>
              <strong>This Location is archived.</strong> It doesn&apos;t appear
              in the main directory or duplicate detection. Records at this
              site still work. Restore to bring it back.
            </span>
          </Box>
        )}

        <SitePlanActivityPanel profiles={profiles} />

        <Flex sx={{ justifyContent: "flex-end", mb: 16, gap: "8px", flexWrap: "wrap" }}>
          {/* Session 24: Location-level archive / restore / delete. Restore
              only shows on archived Locations; Archive + Delete on active. */}
          {location?.deletedAt ? (
            <BasicButton
              type="button"
              scheme="secondary"
              outline
              onClick={handleRestoreLocation}
              disabled={locationBusy}
              styles={{ margin: "0" }}
              keepText
            >
              {locationBusy ? "Working\u2026" : "Restore location"}
            </BasicButton>
          ) : (
            <>
              <BasicButton
                type="button"
                scheme="secondary"
                outline
                onClick={handleArchiveLocation}
                disabled={locationBusy}
                styles={{ margin: "0" }}
                keepText
              >
                {locationBusy ? "Working\u2026" : "Archive location"}
              </BasicButton>
              <BasicButton
                type="button"
                scheme="secondary"
                outline
                onClick={handleDeleteLocation}
                disabled={locationBusy}
                styles={{ margin: "0" }}
                keepText
              >
                Delete
              </BasicButton>
            </>
          )}
          <BasicButton
            type="button"
            scheme="secondary"
            outline
            onClick={handleImportHere}
            disabled={busyId === "__import__"}
            styles={{ margin: "0" }}
            keepText
          >
            {busyId === "__import__" ? "Importing\u2026" : "Import map"}
          </BasicButton>
          {maps.length > 0 && (
            <BasicButton
              type="button"
              scheme="secondary"
              outline
              onClick={handleExportAll}
              disabled={busyId === "__export_all__"}
              styles={{ margin: "0" }}
              keepText
            >
              {busyId === "__export_all__" ? "Exporting\u2026" : "Export all maps"}
            </BasicButton>
          )}
          <BasicButton
            onClick={handleNewMap}
            $iconHeight="10px"
            disabled={!location}
          >
            <PlusIcon fill="#fff" />
            New map at this site
          </BasicButton>
        </Flex>

        {chipRowInfo.clients.length > 1 && (
          <div className={styles.profilePickerRow}>
            <span
              className={styles.profilePickerLabel}
              title={"Clients with activity (maps or records) at this site. Contacts for each client now live on individual map versions in the editor; this row is for per-client notes only."}
            >
              Clients at this site:
            </span>
            {chipRowInfo.clients.map((c) => {
              // Local narrowing for profile (same reason as above).
              const profile = c.profile;
              const clientMapCount = maps.filter(
                (m) => m.clientId === c.clientId,
              ).length;
              const clientRecordCount =
                records.quoteRequests.filter((r) => r.clientId === c.clientId)
                  .length +
                records.quotes.filter((r) => r.clientId === c.clientId)
                  .length +
                records.projects.filter((r) => r.clientId === c.clientId)
                  .length +
                records.invoices.filter((r) => r.clientId === c.clientId)
                  .length +
                records.purchaseOrders.filter(
                  (r) => r.clientId === c.clientId,
                ).length;
              return (
                <span key={c.clientId} className={styles.profileChipGroup}>
                  {/* Session 26 Batch #9: chip is display-only. Filter
                      behavior (clicking to scope maps/records to a
                      client) is gone along with the profile-driven
                      model. Aggregation is now the source of truth,
                      and reps who need a client-scoped view can jump
                      into a specific map or record directly. The
                      sibling Edit affordance stays so per-client
                      notes are still editable on multi-client sites.
                      Single-client sites don't render this row at all
                      — the client name is already visible on every
                      map's badge, so the aggregate is redundant. */}
                  <span
                    className={
                      styles.profileChip + " " + styles.profileChipStatic
                    }
                    title={c.clientName}
                  >
                    {c.clientName}
                    <span className={styles.profileChipCount}>
                      {" \u00b7 " +
                        clientMapCount +
                        " map" +
                        (clientMapCount === 1 ? "" : "s") +
                        " \u00b7 " +
                        clientRecordCount +
                        " records"}
                    </span>
                  </span>
                  {profile && (
                    <button
                      type="button"
                      className={styles.profileEditButton}
                      onClick={() => setEditingProfileId(profile.id)}
                      title={"Edit notes for " + c.clientName}
                      aria-label={"Edit notes for " + c.clientName}
                    >
                      Edit notes
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        )}

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Site notes</h3>
          </div>
          {/* N1 - per-note site notes with client-portal visibility
              (replaces the single siteNotes textarea). Internal notes are
              hidden from clients unless staff share them; client notes
              are always visible to staff. Visibility rules enforced
              server-side. */}
          {location?.id && <LocationNotes locationId={location.id} />}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Maps at this site</h3>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <label
                style={{
                  fontSize: 12,
                  color: "#5D5E62",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
                Show archived
              </label>
              <span className={styles.sectionCount}>
                {filteredMaps.length} map{filteredMaps.length === 1 ? "" : "s"}
                {selectedProfile ? ` · ${selectedProfile.clientName}` : ""}
              </span>
            </div>
          </div>

          {loadingMaps ? (
            <div className={styles.emptyState}>Loading maps{"\u2026"}</div>
          ) : filteredMaps.length === 0 ? (
            <div className={styles.emptyState}>
              {selectedProfile ? (
                <>
                  No maps for{" "}
                  <strong>{selectedProfile.clientName}</strong> at this site
                  yet.{" "}
                  <button
                    className={styles.linkButton}
                    onClick={() => setSelectedProfileId(null)}
                  >
                    Show all clients
                  </button>
                </>
              ) : (
                <>
                  No maps saved at this site yet.{" "}
                  <button className={styles.linkButton} onClick={handleNewMap}>
                    Draw the first one {"\u2192"}
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className={styles.mapList}>
              {filteredMaps.map((m) => (
                <div
                  key={m.mapGroupId}
                  className={styles.mapRow}
                  style={m.archived ? { opacity: 0.7 } : undefined}
                >
                  <div className={styles.mapRowMain}>
                    <button
                      className={styles.mapNameButton}
                      onClick={() => handleOpen(m)}
                      title="Open in mulch calculator"
                      disabled={m.archived}
                    >
                      {m.name || "Untitled map"}
                    </button>
                    <div className={styles.mapRowMeta}>
                      {m.archived && (
                        <span className={styles.internalBadge}>Archived</span>
                      )}
                      {m.clientName ? (
                        <span className={styles.clientBadge}>{m.clientName}</span>
                      ) : (
                        !m.archived && (
                          <span className={styles.internalBadge}>Internal</span>
                        )
                      )}
                      <span>v{m.version}</span>
                      <span>
                        {m.zoneCount} zone{m.zoneCount === 1 ? "" : "s"}
                      </span>
                      <span>{m.totalCuYd.toFixed(1)} cu yd</span>
                      <span>Edited {moment(m.updatedAt).fromNow()}</span>
                    </div>
                  </div>
                  <div className={styles.mapRowActions}>
                    {m.archived ? (
                      <button
                        className={styles.actionButton}
                        onClick={() => handleRestore(m)}
                        disabled={busyId === m.mapGroupId}
                        title="Bring this map back — all versions restored"
                      >
                        {busyId === m.mapGroupId ? "Restoring\u2026" : "Restore"}
                      </button>
                    ) : (
                      <>
                        <button
                          className={styles.actionButton}
                          onClick={() => handleOpen(m)}
                          disabled={busyId === m.mapGroupId}
                        >
                          Open
                        </button>
                        <button
                          className={styles.actionButton}
                          onClick={() => handleDuplicate(m)}
                          disabled={busyId === m.mapGroupId}
                          title="Fork this map into a new independent copy"
                        >
                          Duplicate
                        </button>
                        <button
                          className={styles.actionButton}
                          onClick={() => handleExportOne(m)}
                          disabled={busyId === m.mapGroupId}
                          title="Download this map as JSON"
                        >
                          Export
                        </button>
                        <button
                          className={`${styles.actionButton} ${styles.actionButtonDanger}`}
                          onClick={() => handleArchive(m)}
                          disabled={busyId === m.mapGroupId}
                        >
                          Archive
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && <Box className={styles.errorBanner}>{error}</Box>}

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Records at this site</h3>
            <span className={styles.sectionCount}>
              {filteredRecordsTotal} total
              {selectedProfile ? ` · ${selectedProfile.clientName}` : ""}
            </span>
          </div>

          {loadingRecords ? (
            <div className={styles.emptyState}>Loading records{"\u2026"}</div>
          ) : filteredRecordsTotal === 0 ? (
            <div className={styles.emptyState}>
              {selectedProfile ? (
                <>
                  No records for{" "}
                  <strong>{selectedProfile.clientName}</strong> at this site
                  yet.{" "}
                  <button
                    className={styles.linkButton}
                    onClick={() => setSelectedProfileId(null)}
                  >
                    Show all clients
                  </button>
                </>
              ) : (
                <>No RFQs, quotes, projects, invoices, or POs at this site yet.</>
              )}
            </div>
          ) : (
            <>
              {filteredRecords.quoteRequests.length > 0 && (
                <RecordGroup
                  label="RFQs"
                  prefix="GMR"
                  color="#DBEAFE"
                  textColor="#1D4ED8"
                  items={filteredRecords.quoteRequests}
                  onOpen={(r) => navigate(`/quote-requests/${r.id}`)}
                  onAttach={(r) =>
                    setAttachTarget({
                      recordType: "quote_request",
                      recordId: r.id,
                      currentMulchMapId: r.mulchMapId ?? null,
                    })
                  }
                  statusLabel={rfqStatusLabel}
                />
              )}
              {filteredRecords.quotes.length > 0 && (
                <RecordGroup
                  label="Quotes"
                  prefix="GMQ"
                  color="#DCFCE7"
                  textColor="#15803D"
                  items={filteredRecords.quotes}
                  onOpen={(r) => navigate(`/quotes/${r.id}`)}
                  onAttach={(r) =>
                    setAttachTarget({
                      recordType: "quote",
                      recordId: r.id,
                      currentMulchMapId: r.mulchMapId ?? null,
                    })
                  }
                  statusLabel={quoteStatusLabel}
                />
              )}
              {filteredRecords.projects.length > 0 && (
                <RecordGroup
                  label="Projects"
                  prefix="GMP"
                  color="#FEF3C7"
                  textColor="#B45309"
                  items={filteredRecords.projects}
                  onOpen={(r) => navigate(`/projects/${r.id}`)}
                  onAttach={(r) =>
                    setAttachTarget({
                      recordType: "project",
                      recordId: r.id,
                      currentMulchMapId: r.mulchMapId ?? null,
                    })
                  }
                  statusLabel={(r) => r.statusName || "\u2014"}
                />
              )}
              {filteredRecords.invoices.length > 0 && (
                <RecordGroup
                  label="Invoices"
                  prefix="GMI"
                  color="#F3E8FF"
                  textColor="#6D28D9"
                  items={filteredRecords.invoices}
                  onOpen={(r) => navigate(`/invoices/${r.id}`)}
                  onAttach={(r) =>
                    setAttachTarget({
                      recordType: "invoice",
                      recordId: r.id,
                      currentMulchMapId: r.mulchMapId ?? null,
                    })
                  }
                  statusLabel={invoiceStatusLabel}
                />
              )}
              {filteredRecords.purchaseOrders.length > 0 && (
                <RecordGroup
                  label="POs"
                  prefix="GMO"
                  color="#DBEAFE"
                  textColor="#1E40AF"
                  items={filteredRecords.purchaseOrders}
                  onOpen={(r) => navigate(`/purchase-orders/${r.id}`)}
                  // POs inherit their Map from the project they belong to —
                  // there's no direct PO<->Map attach flow. Omit onAttach so
                  // RecordGroup hides the button (see conditional below).
                  onAttach={null}
                  statusLabel={poStatusLabel}
                />
              )}
            </>
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Work orders at this site</h3>
            <span className={styles.sectionCount}>
              {filteredWorkOrders.length} total
              {selectedProfile ? ` · ${selectedProfile.clientName}` : ""}
            </span>
          </div>

          {loadingWorkOrders ? (
            <div className={styles.emptyState}>Loading work orders{"\u2026"}</div>
          ) : filteredWorkOrders.length === 0 ? (
            <div className={styles.emptyState}>
              No work orders at this site yet. Work orders are raised from a
              maintenance plan and show here once this site is matched to one.
            </div>
          ) : (
            <div className={styles.recordGroup}>
              <div className={styles.recordList}>
                {filteredWorkOrders.map((w) => (
                  <div key={w.id} className={styles.recordRow}>
                    <div className={styles.recordRowMain}>
                      <button
                        className={styles.recordIdButton}
                        onClick={() =>
                          navigate(`/maintenance/work-orders/${w.id}`)
                        }
                        title="Open work order"
                      >
                        {w.externalId || "Work order"}
                      </button>
                      <div className={styles.recordRowMeta}>
                        <span className={styles.recordProjectName}>
                          {w.servicePlan?.name || "One-off work order"}
                        </span>
                        {w.lcp?.client?.name && (
                          <span className={styles.recordClient}>
                            · {w.lcp.client.name}
                          </span>
                        )}
                        <span className={styles.recordStatus}>
                          · {woStageLabel(w)}
                        </span>
                        {w.service?.name && (
                          <span className={styles.recordStatus}>
                            · {w.service.name}
                          </span>
                        )}
                        <span className={styles.recordDate}>
                          ·{" "}
                          {moment(w.receivedAt || w.createdAt).format(
                            "MMM D, YYYY",
                          )}
                        </span>
                      </div>
                    </div>
                    <div className={styles.recordRowActions}>
                      <button
                        className={styles.actionButton}
                        onClick={() =>
                          navigate(`/maintenance/work-orders/${w.id}`)
                        }
                      >
                        Open
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Page>

      {attachTarget && id && (
        <AttachMapModal
          locationId={id}
          recordType={attachTarget.recordType}
          recordId={attachTarget.recordId}
          currentMulchMapId={attachTarget.currentMulchMapId}
          onClose={() => setAttachTarget(null)}
          onAttached={() => {
            setAttachTarget(null);
            refetchRecords();
          }}
        />
      )}

      {editingProfileId && (
        <ProfileEditModal
          profileId={editingProfileId}
          onClose={() => setEditingProfileId(null)}
          onSaved={refetchProfiles}
        />
      )}

      {pendingImport && (
        <ImportMapDedupModal
          siteLabel={location?.name || location?.address?.line1 || "this site"}
          entries={pendingImport.entries}
          onCancel={() => setPendingImport(null)}
          onConfirm={async (choices) => {
            const payload = pendingImport.payload;
            setPendingImport(null);
            await finalizeImport(payload, choices);
          }}
        />
      )}
    </>
  );
};

/**
 * Route entry for `/locations/:id`. Role-branches the same way Projects
 * and Work Orders do: a client gets the lean read-only
 * `ClientLocationDetail` (site header + their work orders at the site);
 * staff get the full operator console above. The operator component —
 * with its archive/delete/merge, map import/export, and the RFQ / Quote /
 * Project / Invoice / PO records chain, all of which are
 * `@Permissions(["isCompany"])` server-side — is never mounted for a
 * client, so none of those staff-only fetches ever fire on a client
 * token. `getLocationById` enforces org ownership server-side (404, not
 * 403, when not theirs), so a client can only ever open their own sites.
 */
function LocationDetail() {
  const { user } = useContext<any>(UserContext);
  // Until the user resolves, render nothing rather than flashing the
  // operator console at a client (or vice versa).
  if (!user) return null;
  if (user.accessLevel !== "isCompany") return <ClientLocationDetail />;
  return <OperatorLocationDetail />;
}

export default LocationDetail;

// ---------------------------------------------------------------------------
// Records section — sub-components + status label helpers
// ---------------------------------------------------------------------------

const woStageLabel = (w: any): string => {
  // A cancelled/rejected WO reads as "Cancelled" regardless of how far
  // its chain got; otherwise use the backend-derived stage label.
  if (w?.status === "rejected") return "Cancelled";
  return w?.stageLabel || "Received";
};

const rfqStatusLabel = (r: any): string => {
  switch (r.status) {
    case 1: return "New";
    case 2: return "Quoted";
    case 3: return "Accepted";
    case 4: return "Closed";
    case 5: return "Canceled";
    case 6: return "Expired";
    default: return "Estimated";
  }
};

const quoteStatusLabel = (r: any): string => {
  switch (r.status) {
    case 1: return "Accepted";
    case 2: return "Denied";
    case 3: return "Draft";
    case 4: return "Expired";
    case 5: return "Superseded";
    case 6: return "Price Locked";
    default: return "New";
  }
};

const invoiceStatusLabel = (r: any): string => {
  // Same wording as every other invoice pill (InvoiceStatusBadge).
  const d = getInvoiceStatusDisplay(r, { isStaff: true });
  return d.tag ? d.label + " \u00b7 " + d.tag.text : d.label;
};

// Session 24: PO status enum from PurchaseOrder entity
// (0 draft, 1 sent, 2 accepted, 3 completed, 4 denied, 5 cancelled, 6 replaced)
const poStatusLabel = (r: any): string => {
  switch (r.status) {
    case 1: return "Sent";
    case 2: return "Accepted";
    case 3: return "Completed";
    case 4: return "Denied";
    case 5: return "Cancelled";
    case 6: return "Replaced";
    default: return "Draft";
  }
};

interface RecordGroupProps {
  label: string;
  prefix: string;
  color: string;
  textColor: string;
  items: any[];
  onOpen: (r: any) => void;
  // Session 24: null when the record type has no direct Map attach flow
  // (POs inherit from their project).
  onAttach: ((r: any) => void) | null;
  statusLabel: (r: any) => string;
}

const RecordGroup: React.FC<RecordGroupProps> = ({
  label,
  prefix,
  color,
  textColor,
  items,
  onOpen,
  onAttach,
  statusLabel,
}) => {
  return (
    <div className={styles.recordGroup}>
      <div className={styles.recordGroupHeader}>
        <span
          className={styles.recordTypeBadge}
          style={{ background: color, color: textColor }}
        >
          {label}
        </span>
        <span className={styles.recordGroupCount}>({items.length})</span>
      </div>
      <div className={styles.recordList}>
        {items.map((r) => (
          <div key={r.id} className={styles.recordRow}>
            <div className={styles.recordRowMain}>
              <button
                className={styles.recordIdButton}
                onClick={() => onOpen(r)}
                title="Open record"
              >
                {prefix}-{String(r.sequence ?? 0).padStart(5, "0")}
              </button>
              <div className={styles.recordRowMeta}>
                <span className={styles.recordProjectName}>
                  {r.projectName || "Instant Estimate"}
                </span>
                {r.clientName && (
                  <span className={styles.recordClient}>· {r.clientName}</span>
                )}
                <span className={styles.recordStatus}>· {statusLabel(r)}</span>
                <span className={styles.recordDate}>
                  · {moment(r.createdAt).format("MMM D, YYYY")}
                </span>
              </div>
              <div className={styles.recordMapLine}>
                {r.mulchMapId && r.mulchMapName ? (
                  <>
                    <span className={styles.attachedDot} /> Attached:{" "}
                    <strong>{r.mulchMapName}</strong>
                    {r.mulchMapVersion ? ` · v${r.mulchMapVersion}` : ""}
                  </>
                ) : (
                  <span className={styles.noMap}>No map attached</span>
                )}
              </div>
            </div>
            <div className={styles.recordRowActions}>
              <button
                className={styles.actionButton}
                onClick={() => onOpen(r)}
              >
                Open
              </button>
              {onAttach && (
                <button
                  className={styles.actionButton}
                  onClick={() => onAttach(r)}
                >
                  {r.mulchMapId ? "Change map" : "Attach map"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
